import compose from 'recompose/compose';
import withProps from 'recompose/withProps';
import withHandlers from 'recompose/withHandlers';
import withState from 'recompose/withState';
import { skus } from 'src/common/config/product';
import { withFormik } from 'formik';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { withFilterOption } from '../../data-providers/filter';
import { trackFilterChange } from '../../../analytics/category/filter';

const { LMN, TJH, KOY, TBT, MGT, TLB, SCS, MLM, TWR, TPW, WAY, MMK } = skus;

/* eslint-disable max-len */
export const AGE_FILTERS = {
  '0-3': [ LMN, MLM, TLB, TWR, SCS, TBT ],
  '3-6': [ SCS, LMN, TBT, TWR, KOY, TJH, MMK, WAY ],
  '6-12' : [ MGT, TPW, WAY, MMK, LMN, TBT, TWR, KOY, TJH, SCS ],
};
export const OCCASION_FILTERS = {
  birthdays: [ TBT, LMN, MGT, TLB, TWR, MLM, KOY, TJH, WAY, TPW, MMK ],
  christmas: [ SCS, LMN, TLB, MGT, TWR, MLM, KOY, TJH, WAY, TPW, MMK ],
  'christening-newborn': [ LMN, TLB, TWR, TBT, MLM ],
  'just-because': [ LMN, MGT, MLM, WAY, TPW, MMK, TWR, KOY, TJH ],
};
export const ORDER_BY = {
  popularity: [ LMN, WAY, TWR, MGT, TBT, MLM, KOY, TLB, TPW, TJH, MMK, SCS ],
  newness: [ WAY, TWR, MMK, TPW, MLM, MGT, TBT, TLB, KOY, TJH, LMN ],
  '0-3': [ MLM, LMN, TLB, TWR, TBT, SCS ],
  '3-6': [ TWR, LMN, TBT, WAY, KOY, TJH, MMK, SCS ],
  '6-12': [ MGT, WAY, TPW, MMK, LMN, TBT, TWR, KOY, TJH, SCS ],
  birthdays: [ TBT, LMN, MGT, WAY, TLB, TWR, MLM, KOY, TJH, TPW, MMK ],
  christmas: [ SCS, LMN, TLB, MGT, TWR, MLM, KOY, TJH, WAY, TPW, MMK ],
  'christening-newborn': [ TLB, LMN, TWR, TBT, MLM ],
  'just-because': [ TWR, MGT, LMN, MLM, WAY, TPW, MMK, KOY, TJH ],
};
const OPTIONS = [
  {
    filter: 'ages',
    id: 'all',
    label: 'All ages'
  },
  {
    filter: 'ages',
    id: '0-3',
    label: '0-3',
  },
  {
    filter: 'ages',
    id: '3-6',
    label: '3-6',
  },
  {
    filter: 'ages',
    id: '6-12',
    label: '6-12',
  },
  {
    filter: 'occasions',
    id: 'all',
    label: 'All occasions'
  },
  {
    filter: 'occasions',
    id: 'christmas',
    label: 'Christmas',
  },
  {
    filter: 'occasions',
    id: 'birthdays',
    label: 'Birthdays',
  },
  {
    filter: 'occasions',
    id: 'christening-newborn',
    label: 'Christening and newborn',
  },
  {
    filter: 'occasions',
    id: 'just-because',
    label: 'Just because',
  },
  {
    filter: 'order',
    id: 'none',
    label: 'Recommended',
  },
  {
    filter: 'order',
    id: 'popularity',
    label: 'Popularity',
  },
  {
    filter: 'order',
    id: 'price',
    label: 'Price',
  },
  {
    filter: 'order',
    id: 'newness',
    label: 'Newness',
  },
  {
    filter: 'order',
    id: 'custom',
    label: 'Custom',
    hidden: true
  },
];
/* eslint-enable max-len */

export const getSelectedProductIds = (currentIds, newIds) => {
  return intersection(currentIds, newIds);
};

export const getProductIds = productIds => {
  if (!isEmpty(productIds)) {
    return productIds;
  }
  return false;
};

export const updateFilterOptions = (currentFilter, currentFilterProductIds, compareFilterProductIds) => option => {
  if (option.filter === currentFilter || option.filter === 'order' || option.id === 'all') {
    return option;
  }
  const availableProducts = getSelectedProductIds(currentFilterProductIds, compareFilterProductIds[option.id]);
  if (isEmpty(availableProducts)) {
    return {
      ...option,
      disabled: true,
    };
  }
  return {
    ...option,
    disabled: false,
  };
};

export const getSortId = (sortList = [], currentId, nextId) => {
  if (!sortList.includes(currentId) && sortList.includes(nextId)) {
    return 1;
  }
  if (sortList.includes(currentId) && !sortList.includes(nextId)) {
    return -1;
  }
  if (!sortList.includes(currentId) && !sortList.includes(nextId)) {
    return 1;
  }
  if (sortList.indexOf(currentId) < sortList.indexOf(nextId)) {
    return -1;
  }
  if (sortList.indexOf(currentId) > sortList.indexOf(nextId)) {
    return 1;
  }
  return 0;
};

export const getSortByPrice = (currentProduct, nextProduct) => {
  const currentProductPrice = parseFloat(currentProduct.price.amount, 10);
  const nextProductPrice = parseFloat(nextProduct.price.amount, 10);
  if (currentProductPrice < nextProductPrice) {
    return -1;
  }
  if (currentProductPrice > nextProductPrice) {
    return 1;
  }
  return 0;
};

export const getSortByRelease = (currentBadge, nextBadge) => {
  switch (currentBadge) {
    case 'latest release':
      return -1;
    case 'new':
      if (nextBadge === 'latest release') {
        return 1;
      }
      return -1;
    default:
      if ((nextBadge === 'latest release' || nextBadge === 'new') || !currentBadge && nextBadge) {
        return 1;
      }
      if (currentBadge && !nextBadge) {
        return -1;
      }
      return 0;
  }
};

export const sortItems = (value, orderBy, defaultIds) => (current, next) => {
  const currentProductId = current.productId;
  const nextProductId = next.productId;

  switch (value) {
    case 'none':
      return getSortId(defaultIds, currentProductId, nextProductId);
    case 'price':
      return getSortByPrice(current, next);
    default:
      return getSortId(orderBy, currentProductId, nextProductId);
  }
};

export const withFiltering = compose(
  withFilterOption,
  withProps(props => ({
    defaultItems: props.items,
    filter: {
      title: 'Find the perfect book...',
      ageLabel: 'How old are they?',
      occasionsLabel: 'What are you buying it for?',
      orderLabel: 'Order by',
      itemsShowing: 'Showing %{itemsVisible} books'
    },
  })),
  withFormik({
    mapPropsToValues: () => ({
      ages: 'all',
      occasions: 'all',
      order: 'none'
    }),
  }),
  withState('filterOptions', 'updateFilterItems', OPTIONS),
  withState('items', 'updateItems', props => props.items),
  withHandlers({
    handleChange: props => event => {
      const defaultProductIds = props.defaultItems.map(item => item.productId);
      const currentElement = event.target.name;
      const currentValue = event.target.value;
      const currentAgeValue = props.values.ages;
      const currentOccasionValue = props.values.occasions;
      const currentOrderValue = props.values.order;

      const currentAgeFilter = getProductIds(AGE_FILTERS[currentAgeValue]) || defaultProductIds;
      const currentOccasionFilter = getProductIds(OCCASION_FILTERS[currentOccasionValue]) || defaultProductIds;

      let filter = [];
      let filteredProducts = [];
      let filterOptions = props.filterOptions;
      let items = cloneDeep(props.defaultItems);

      if (currentOrderValue !== 'none') {
        items.sort(sortItems(currentOrderValue, ORDER_BY[currentOrderValue], defaultProductIds));
      }

      switch (currentElement) {
        case 'ages': {
          filter = getProductIds(AGE_FILTERS[currentValue]) || defaultProductIds;
          filteredProducts = getSelectedProductIds(currentOccasionFilter, filter);
          filterOptions = filterOptions.map(updateFilterOptions('ages', filter, OCCASION_FILTERS));

          if (currentOrderValue === 'none') {
            if (currentValue === 'all') {
              items = items.sort(sortItems('none', [], defaultProductIds));
            } else {
              items = items.sort(sortItems(currentValue, ORDER_BY[currentValue], defaultProductIds));
            }
          }
          break;
        }
        case 'occasions': {
          filter = getProductIds(OCCASION_FILTERS[currentValue]) || defaultProductIds;
          filteredProducts = getSelectedProductIds(currentAgeFilter, filter);
          filterOptions = filterOptions.map(updateFilterOptions('occasions', filter, AGE_FILTERS));

          if (currentOrderValue === 'none') {
            if (currentValue === 'all') {
              items = items.sort(sortItems('none', [], defaultProductIds));
            } else {
              items = items.sort(sortItems(currentValue, ORDER_BY[currentValue], defaultProductIds));
            }
          }

          break;
        }
        case 'order': {
          filteredProducts = getSelectedProductIds(currentAgeFilter, currentOccasionFilter);
          items = items.sort(sortItems(currentValue, ORDER_BY[currentValue], defaultProductIds));
          break;
        }
        default: {
          filteredProducts = getSelectedProductIds(currentAgeFilter, currentOccasionFilter);
          break;
        }
      }


      if (!isEmpty(filteredProducts)) {
        items = items.filter(item => filteredProducts.includes(item.productId));
      }

      props.setFilterValues({
        ...props.values,
        [currentElement]: currentValue,
        current: currentElement
      });
      props.updateFilterItems(filterOptions);
      props.handleChange(event);
      props.updateItems(items);

      const slugRegex = /\/([^\/]+)\/?$/;
      const pathname = window.location.pathname;
      const variant = pathname.match(slugRegex)[1];

      trackFilterChange(variant, currentElement, currentValue);
    },
  })
);



// WEBPACK FOOTER //
// ./src/client/js/view/components/ProductList/helpers.js